import { DestinationEvent } from '@eventbrite/event-renderer';
import {
    deepKeysToCamel,
    transformUtil,
} from '@eventbrite/transformation-utils';
import { arrayOf, Schema } from 'normalizr';
import { ONLINE_BANNER_URL } from '../constants/constants';
import {
    ApiEventSearch,
    ApiSearchData,
    Branding,
    Event,
    EventSearch,
    PaginationData,
} from '../types';
import { eventsCardInfoLookup } from './general';

export const getTransformedContent = (key: any, response: any) => {
    /* Due to how the normalizr library is handling this instantiation
   server side, necessary to do an additional interpolation step. */
    const content = new Schema(`${key}`);

    return transformUtil({
        response,
        schema: {
            [key]: arrayOf(content),
        },
    });
};

export type SearchResponseEvents = {
    events: SearchResponseEventsData;
    pagination: PaginationData;
};

export type SearchResponseEventsData = {
    organic: Event[];
    promoted: Event[];
    topMatches: Event[];
};

export const parseSearchDataToEvents = (
    searchData: ApiSearchData,
    context: { section?: string; currencyFormat?: string },
): SearchResponseEvents => {
    const { events } = searchData;

    return {
        events: {
            organic: parseApiEventData(events.results, context),
            promoted: parseApiEventData(events.promoted_results, context),
            topMatches: parseApiEventData(events.top_match, context),
        },
        pagination: {
            page: events.pagination.page_number,
            pageSize: events.pagination.page_size,
            pageCount: events.pagination.page_count,
            objectCount: events.pagination.object_count,
            continuation: events.pagination.continuation,
        },
    };
};

function parseApiEventData(
    events: DestinationEvent[] | undefined = [],
    context: { section?: string; currencyFormat?: string },
): Event[] {
    const normalizedEvents = events.map(deepKeysToCamel);
    // TODO: Evaluate if this could be replaced with transformDestinationEvent
    // transformDestinationEvent lacks venue information needed for the map as of 29/03/2023
    return eventsCardInfoLookup({
        bucketsContent: normalizedEvents,
        ...context,
    });
}

export const parseSearchCriteria = (
    apiSearchCriteria: ApiEventSearch,
): EventSearch => {
    return deepKeysToCamel(apiSearchCriteria);
};

export const transformBrandingResponse = (
    branding: Branding,
    isOnline?: boolean,
) => {
    const {
        attribution: {
            name: artistName,
            summary: artistSummary,
            image: { original: { url: artistImageUrl } = {} as any } = {},
            social_links: artistSocialLinks,
        } = {} as any,
        header: {
            image: {
                original: { url: headerImageUrl } = {} as any,
                url: headerSmallImageUrl,
            } = {} as any,
        } = {},
        middle: {
            image: {
                original: { url: middleImageUrl } = {} as any,
                url: middleSmallImageUrl,
            } = {} as any,
        } = {},
        footer: {
            image: {
                original: { url: footerImageUrl } = {} as any,
                url: footerSmallImageUrl,
            } = {} as any,
        } = {},
        decoration_top_left: {
            image: { original: { url: decorationTopLeftUrl } = {} as any } = {},
        } = {},
        decoration_top_right: {
            image: {
                original: { url: decorationTopRightUrl } = {} as any,
            } = {},
        } = {},
        decoration_bottom_left: {
            image: {
                original: { url: decorationBottomLeftUrl } = {} as any,
            } = {},
        } = {},
        decoration_bottom_right: {
            image: {
                original: { url: decorationBottomRightUrl } = {} as any,
            } = {},
        } = {},
    } = branding;

    let onlineHeaderImage = null;

    if (isOnline) {
        onlineHeaderImage = ONLINE_BANNER_URL;
    }

    return {
        artistName,
        artistSummary,
        artistImageUrl,
        headerImageUrl: onlineHeaderImage || headerImageUrl,
        headerSmallImageUrl: onlineHeaderImage || headerSmallImageUrl,
        middleImageUrl,
        middleSmallImageUrl,
        footerImageUrl,
        footerSmallImageUrl,
        decorationTopLeftUrl,
        decorationTopRightUrl,
        decorationBottomLeftUrl,
        decorationBottomRightUrl,
        artistSocialLinks,
    };
};
